import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useScreenRecorder } from 'contexts/ScreenRecorderContext';
import { FormattedMessage, useIntl } from 'react-intl';
import Notice from 'components/Notice';
import RecordingSuccessModal from './RecordingSuccessModal';
import { videoStorage } from '../../../utils/videoStorage';

const ScreenRecorderManager: React.FC = () => {
  const intl = useIntl();
  const {
    recordingStatus,
    stopRecording,
    requestStopRecording,
    cancelStopRecording,
    isConfirmStopOpen,
    isShowStartWarningModal,
    closeStartWarningModal,
    recordedVideo,
    setRecordedVideo,
  } = useScreenRecorder();
  const history = useHistory();
  const location = useLocation();

  // Local state to manage visible modal independently of global recordedVideo state
  // This prevents the "click twice to close" bug caused by state synchronization
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [userClosedModal, setUserClosedModal] = useState(false);

  useEffect(() => {
    if (recordedVideo && !showSuccessModal && !userClosedModal) {
      setShowSuccessModal(true);

      // Auto-redirect to session page if not already there
      // This ensures the SessionContent component mounts and opens the form
      const sessionPath = `/courses/${recordedVideo.courseId}/sessions`;
      if (recordedVideo.courseId && !location.pathname.includes(sessionPath)) {
        history.push(sessionPath);
      }
    }
    // Reset userClosedModal when recordedVideo changes to a new recording
    if (!recordedVideo) {
      setUserClosedModal(false);
    }
  }, [
    recordedVideo,
    showSuccessModal,
    userClosedModal,
    location.pathname,
    history,
  ]);

  const handleClose = async () => {
    setShowSuccessModal(false);
    setUserClosedModal(true); // Mark that user manually closed the modal

    // Local storage deletion logic removed here
    // based on user request to keep videos permanently.
    if (recordedVideo) {
      // Also clear emergency save flag from localStorage if exists
      try {
        const emergencyData = localStorage.getItem('emergency_video_save');
        if (emergencyData) {
          const data = JSON.parse(emergencyData);
          if (data.sessionId === recordedVideo.sessionId) {
            localStorage.removeItem('emergency_video_save');
          }
        }
      } catch (err) {
        console.error(
          '[ScreenRecorder] Failed to clear emergency save flag:',
          err,
        );
      }
    }

    // DON'T clear recordedVideo here!
    // Video needs to stay in state for upload to session content
    // It will be cleared when user saves session content or navigates away
  };

  const downloadRecording = async () => {
    if (recordedVideo) {
      const a = document.createElement('a');
      a.href = recordedVideo.url;
      const dateStr = new Date().toISOString().slice(0, 10);
      const safeTitle =
        recordedVideo.sessionTitle.replace(/[^a-z0-9]/gi, '_') || 'Session';
      const extension = 'webm';
      a.download = `Antoree_Recording_${safeTitle}_${dateStr}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Local storage deletion removed after download.

      // Also clear emergency save flag from localStorage if exists
      try {
        const emergencyData = localStorage.getItem('emergency_video_save');
        if (emergencyData) {
          const data = JSON.parse(emergencyData);
          if (data.sessionId === recordedVideo.sessionId) {
            localStorage.removeItem('emergency_video_save');
          }
        }
      } catch (err) {
        console.error(
          '[ScreenRecorder] Failed to clear emergency save flag:',
          err,
        );
      }

      handleClose();
      // Clear recordedVideo after download since user has a copy
      setRecordedVideo(null);
      setUserClosedModal(false); // Reset flag since we're clearing the video
    }
  };

  // If nothing is happening, don't render
  if (recordingStatus === 'idle' && !showSuccessModal && !isConfirmStopOpen && !isShowStartWarningModal)
    return null;

  return (
    <>
      {/* 0. Start Warning Modal */}
      {isShowStartWarningModal && (
        <Notice
          title={intl.formatMessage({
            id: 'screen_recorder.warning.dont_stop_sharing_title',
            defaultMessage: 'IMPORTANT NOTE: DO NOT CLICK "Stop sharing"',
          })}
          content={intl.formatMessage({
            id: 'screen_recorder.warning.dont_stop_sharing_text',
            defaultMessage: 'Please DO NOT click the "Stop sharing" button at the bottom of the screen as it will stop the recording. You can click "Hide" to hide that bar.',
          })}
          close={closeStartWarningModal}
          func={closeStartWarningModal}
          hideAction={true}
          extraActions={
            <button
              onClick={closeStartWarningModal}
              style={{
                backgroundColor: '#1890ff',
                color: 'white',
                padding: '8px 24px',
                borderRadius: '8px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                margin: '0 auto',
                display: 'block'
              }}
            >
              {intl.formatMessage({ id: 'button.understood', defaultMessage: 'Understood' })}
            </button>
          }
        />
      )}

      {/* 1. Confirmation Notice Modal */}
      {isConfirmStopOpen && (
        <Notice
          title={intl.formatMessage({
            id: 'screen_recorder.confirm_stop_title',
            defaultMessage: 'Confirm Stop Recording',
          })}
          content={intl.formatMessage({
            id: 'screen_recorder.confirm_stop_content',
            defaultMessage:
              'Warning: Are you sure you want to stop recording? Stopping screen sharing / recording will end the recording session and finalize the video saving process.',
          })}
          close={cancelStopRecording}
          func={stopRecording}
        />
      )}

      {/* 2. Simple Recording Bar (Z-index 99999) */}
      {recordingStatus === 'recording' && (
        <div
          style={{
            zIndex: 99999,
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '16px',
            boxShadow:
              '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #fff1f0',
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#ff4d4f',
                borderRadius: '50%',
                animation: 'simple-blink 1s infinite',
              }}
            />
            <span
              style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '14px' }}
            >
              <FormattedMessage
                id="recording.status_recording"
                defaultMessage="RECORDING..."
              />
            </span>
          </div>
          <button
            onClick={requestStopRecording}
            style={{
              backgroundColor: '#ff4d4f',
              color: 'white',
              padding: '8px 20px',
              borderRadius: '8px',
              fontWeight: 'bold',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <FormattedMessage
              id="recording.stop_recording"
              defaultMessage="Stop Recording"
            />
          </button>
        </div>
      )}

      {/* 3. Simple Success Modal (Z-index 5000) */}
      {showSuccessModal && recordedVideo && (
        <RecordingSuccessModal
          recordedVideo={recordedVideo}
          onClose={handleClose}
          onDownload={downloadRecording}
          onConfirmClose={handleClose}
        />
      )}

      <style>{`
        @keyframes simple-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }
      `}</style>
    </>
  );
};

export default ScreenRecorderManager;
